import { Price } from '@brenger/api-client';
import { getActiveLanguageSettings } from './localization';

interface PriceAsString {
  price?: Partial<Price>;
  addPlusMinusIndicator?: boolean;
  withDoubleZeroes?: boolean;
}

export const priceAsString = ({
  price,
  addPlusMinusIndicator = true,
  withDoubleZeroes = false,
}: PriceAsString): string => {
  let amount = price?.amount || 0;

  // When the amount is positive and we want a plus indicator, we need to add it manually
  const indicator = amount >= 0 ? '+' : '-';

  // When the amount is negative, we don't want another "minus" popup, so we need to multiply the amount by -1
  if (amount < 0) {
    amount = amount * -1;
  }

  // get locale settings
  const {
    labels: { short, countryCode, currency },
  } = getActiveLanguageSettings();

  // Check if we need to print only round numbers
  const priceArray = centsToPriceArray(amount);
  // We print round numbers when decimals are 00 and we don't want to explicitly print that
  const showRoundNumbers = priceArray[1] === '00' && !withDoubleZeroes;

  // When max Significant Digits is set, it only prints decimals if there are any, when not set it prints decimals according to currency style
  // Set up international number formatting
  let formattedAmount = new Intl.NumberFormat(`${short}-${countryCode}`, {
    style: 'currency',
    currency: price?.currency || currency,
  }).format(amount / 100);

  // Previous way of stripping with significant digits gave a weird result, it round 235,- to 240,-
  if (showRoundNumbers) {
    formattedAmount = formattedAmount.slice(0, -3);
  }

  // Do we want an indicator?
  // Yes please
  if (addPlusMinusIndicator) {
    return `${indicator} ${formattedAmount}`;
  }
  // No, just the formatted amount please
  return formattedAmount;
};

/**
 * Pricing expresses percentages in basis points: 800 basis points equals 8%.
 */
export const basisPointsAsString = (basisPoints: number): string => {
  const {
    labels: { short, countryCode },
  } = getActiveLanguageSettings();

  return new Intl.NumberFormat(`${short}-${countryCode}`, { maximumFractionDigits: 2 }).format(basisPoints / 100);
};

/**
 * This function returns
 */
export const centsToPriceArray = (cents: number): string[] => {
  if (cents === 0) {
    return ['0', '00'];
  }
  return (cents / 100).toFixed(2).split('.');
};
